import type { DisplayDateFormat, MembershipSummary, TenantMemberProfile } from '@livechat/shared';
import { useEffect, useState } from 'react';

import { DateFormatProvider } from '../date-format.js';

import { TenantCannedResponses } from './TenantCannedResponses.js';
import { TenantChatHistory } from './TenantChatHistory.js';
import { TenantDashboard } from './TenantDashboard.js';
import { TenantInbox } from './TenantInbox.js';
import { TenantMembers } from './TenantMembers.js';
import { TenantReports, type ReportSection } from './TenantReports.js';
import { TenantSettingsWorkspace, type SettingsSection } from './TenantSettingsWorkspace.js';
import { TenantVisitorHistory, TenantVisitors } from './TenantVisitors.js';

export type WorkspaceTab =
	| 'dashboard'
	| 'chats'
	| 'history'
	| 'visitors'
	| 'visitor-history'
	| 'responses'
	| 'reports'
	| 'team'
	| 'settings';

export interface WorkspaceNavigationItem {
	label: string;
	tab: WorkspaceTab;
}

const WORKSPACE_NAVIGATION: WorkspaceNavigationItem[] = [
	{ label: 'Dashboard', tab: 'dashboard' },
	{ label: 'Chats', tab: 'chats' },
	{ label: 'Chat History', tab: 'history' },
	{ label: 'Visitors', tab: 'visitors' },
	{ label: 'Visitor History', tab: 'visitor-history' },
	{ label: 'Team', tab: 'team' },
	{ label: 'Responses', tab: 'responses' },
	{ label: 'Reports', tab: 'reports' },
	{ label: 'Settings', tab: 'settings' },
];

export function workspaceNavigation(
	membership: MembershipSummary,
	isPlatformAdmin = false,
): WorkspaceNavigationItem[] {
	return WORKSPACE_NAVIGATION.filter(({ tab }) => {
		if (!isPlatformAdmin && membership.role === 'agent' && ['team', 'reports'].includes(tab)) {
			return false;
		}
		return true;
	});
}

export function workspaceNavigationItemIsActive(
	item: WorkspaceNavigationItem,
	tab: WorkspaceTab,
): boolean {
	return !['reports', 'settings'].includes(item.tab) && item.tab === tab;
}

export function TenantWorkspace({
	claimInitialConversation,
	membership,
	currentUserId,
	isPlatformAdmin,
	historyVisitorId,
	initialConversationId,
	onActiveConversationChanged,
	onConversationSelected,
	onDepartmentSelected,
	knowledgeCollectionSlug,
	onKnowledgeCollectionSelected,
	onNavigate,
	onOpenMemberSettings,
	onProfileChanged,
	reportSection,
	onMemberDeleted,
	onViewVisitorHistory,
	settingsUserId,
	settingsDepartmentId,
	settingsSection,
	tab,
}: {
	claimInitialConversation?: boolean;
	membership: MembershipSummary;
	currentUserId: string;
	isPlatformAdmin: boolean;
	historyVisitorId?: string;
	initialConversationId?: string;
	onActiveConversationChanged: (conversationId?: string) => void;
	onConversationSelected: (conversationId?: string) => void;
	onDepartmentSelected: (departmentId?: string, replace?: boolean) => void;
	knowledgeCollectionSlug?: string;
	onKnowledgeCollectionSelected?: (slug?: string, replace?: boolean) => void;
	onNavigate: (tab: WorkspaceTab) => void;
	onOpenMemberSettings: (userId: string) => void;
	onProfileChanged: (profile: TenantMemberProfile) => void;
	onMemberDeleted: () => void;
	onViewVisitorHistory: (visitorId: string) => void;
	reportSection: ReportSection;
	settingsUserId?: string;
	settingsDepartmentId?: string;
	settingsSection: SettingsSection;
	tab: WorkspaceTab;
}) {
	const [dateFormat, setDateFormat] = useState<DisplayDateFormat>(
		membership.dateFormat ?? 'dd/mm/yyyy',
	);
	useEffect(() => {
		setDateFormat(membership.dateFormat ?? 'dd/mm/yyyy');
	}, [membership.dateFormat, membership.tenantId]);
	const canSeeTeam = isPlatformAdmin || membership.role !== 'agent';
	const activeTab = workspaceNavigation(membership, isPlatformAdmin).some(
		(item) => item.tab === tab,
	)
		? tab
		: 'dashboard';

	return (
		<DateFormatProvider format={dateFormat}>
			<section
				className={`workspace-section ${['dashboard', 'chats', 'history', 'visitors', 'visitor-history', 'reports', 'settings'].includes(activeTab) ? 'workspace-section-wide' : ''} ${['chats', 'history', 'visitors', 'visitor-history', 'reports'].includes(activeTab) ? 'data-workspace-section' : ''} ${activeTab === 'dashboard' ? 'dashboard-workspace-section' : ''} ${activeTab === 'chats' && initialConversationId ? 'chat-conversation-workspace-section' : ''}`}
			>
				{activeTab === 'dashboard' && (
					<TenantDashboard membership={membership} onNavigate={onNavigate} />
				)}
				{activeTab === 'chats' && (
					<TenantInbox
						claimInitialConversation={claimInitialConversation}
						currentUserId={currentUserId}
						initialConversationId={initialConversationId}
						isPlatformAdmin={isPlatformAdmin}
						membership={membership}
						onActiveConversationChanged={onActiveConversationChanged}
						onConversationSelected={onConversationSelected}
						onViewVisitorHistory={onViewVisitorHistory}
					/>
				)}
				{activeTab === 'history' && (
					<TenantChatHistory
						initialVisitorId={historyVisitorId}
						membership={membership}
						onOpenConversation={onConversationSelected}
					/>
				)}
				{activeTab === 'visitors' && (
					<TenantVisitors
						membership={membership}
						onOpenConversation={onConversationSelected}
					/>
				)}
				{activeTab === 'visitor-history' && (
					<TenantVisitorHistory
						membership={membership}
						onOpenConversation={onConversationSelected}
					/>
				)}
				{activeTab === 'responses' && <TenantCannedResponses membership={membership} />}
				{activeTab === 'reports' && (
					<TenantReports membership={membership} section={reportSection} />
				)}
				{activeTab === 'team' && canSeeTeam && (
					<TenantMembers
						currentUserId={currentUserId}
						isPlatformAdmin={isPlatformAdmin}
						membership={membership}
						onOpenSettings={onOpenMemberSettings}
						embedded
					/>
				)}
				{activeTab === 'settings' && (
					<TenantSettingsWorkspace
						currentUserId={currentUserId}
						departmentId={settingsDepartmentId}
						membership={membership}
						onDateFormatChanged={setDateFormat}
						onDepartmentSelected={onDepartmentSelected}
						knowledgeCollectionSlug={knowledgeCollectionSlug}
						onKnowledgeCollectionSelected={onKnowledgeCollectionSelected}
						onProfileChanged={onProfileChanged}
						onMemberDeleted={onMemberDeleted}
						section={settingsSection}
						targetUserId={settingsUserId}
					/>
				)}
			</section>
		</DateFormatProvider>
	);
}
